import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { useBggNumericFiltersPipeline } from '../hooks/useBggNumericFiltersPipeline'
import { useBggOwnedCollectionFetch } from '../hooks/useBggOwnedCollectionFetch'
import BggNumericFilters from '../Components/BggNumericFilters'
import BggCollectionFiltersAccordion from '../Components/BggCollectionFiltersAccordion'
import { buildOwnedCollectionParams, parseCommaSeparatedUsernames } from '../bggCollectionQuery'
import { FaDice } from 'react-icons/fa'
import {
	Box,
	Input,
	Button,
	Heading,
	Checkbox,
	useToast,
	Icon,
	Text,
	Link,
	VStack,
	SimpleGrid,
	FormControl,
	FormLabel,
	Divider,
	useColorModeValue,
	Spinner,
	Center,
} from '@chakra-ui/react'

import ChosenGame from '../Components/ChosenGame'
import PickerDrawFlip, {
	POOL_CAP,
	STRIP_MAX_W,
	STRIP_CARD_BODY_MAX_H,
	getPickerStripSectionMinHeight,
} from '../Components/PickerDrawFlip'
import RatingSelect from '../Components/RatingSelect'
import { enrichRollPoolWithThingCovers } from '../bggThingCovers'
import { clearThingMetaCache } from '../bggThingMeta'
import { filterPlayerCount, pickRandomSubset, randomInt } from '../Utils'

export default function Picker() {
	const [username, setUsername] = useState('')

	const [minRating, setMinRating] = useState(null)
	const [rating, setRating] = useState(null)
	const [minBGGRating, setMinBGGRating] = useState(null)
	const [numPlayers, setNumPlayers] = useState(null)
	const [minPlayerCount, setMinPlayerCount] = useState(null)
	const [maxPlayerCount, setMaxPlayerCount] = useState(null)
	const [rated, setRated] = useState(false)
	const [played, setPlayed] = useState(false)
	const [comment, setComment] = useState(false)
	const [hideExpansions, setHideExpansions] = useState(false)
	const [maxPlayTime, setMaxPlayTime] = useState('any')

	const [activeCollection, setActivecollection] = useState([])
	const [chosenGame, setChosenGame] = useState()
	const [rollVisual, setRollVisual] = useState(null)
	const [rollCoverLoading, setRollCoverLoading] = useState(false)
	const rollSessionIdRef = useRef(0)
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

	const toast = useToast()
	const pendingFetchOutcomeToastRef = useRef(false)
	const { startFetch, loading } = useBggOwnedCollectionFetch({ maxRetry: 5 })

	const onFilteredList = useCallback(
		(list) => {
			setActivecollection(list)
			if (pendingFetchOutcomeToastRef.current) {
				pendingFetchOutcomeToastRef.current = false
				toast.closeAll()
				if (list.length > 0) {
					toast({
						title: 'Collection loaded',
						description: 'Your filters are applied. Roll again anytime.',
						status: 'success',
						duration: 3000,
						isClosable: true,
					})
					setTimeout(() => {
						const el = document.getElementById('picker-roll-again')
						if (el) el.scrollIntoView({ block: 'start', behavior: 'auto' })
					}, 0)
				} else {
					toast({
						title: 'No games matched',
						description: 'Nothing left after the max playing time filter. Widen the limit or refresh from BGG.',
						status: 'warning',
						duration: 6000,
						isClosable: true,
					})
				}
			}
		},
		[toast]
	)

	const { commitPlayerFiltered, resetPipeline, applyingExtraFilters } = useBggNumericFiltersPipeline(maxPlayTime, onFilteredList)

	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) return
		const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
		const apply = () => setPrefersReducedMotion(!!mq.matches)
		apply()
		if (mq.addEventListener) {
			mq.addEventListener('change', apply)
			return () => mq.removeEventListener('change', apply)
		}
		mq.addListener(apply)
		return () => mq.removeListener(apply)
	}, [])

	const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.300')
	const stripCardBg = useColorModeValue('white', 'bgg.800')
	const stripMuted = useColorModeValue('gray.600', 'gray.400')
	const bodyMuted = useColorModeValue('gray.600', 'bgg.muted')

	const buildRollPool = useCallback((collection) => {
		if (!collection.length) return []
		if (collection.length > POOL_CAP) return pickRandomSubset(collection, POOL_CAP)
		return collection
	}, [])

	const startRoll = useCallback(() => {
		if (!activeCollection.length) {
			setChosenGame(undefined)
			setRollVisual(null)
			setRollCoverLoading(false)
			return
		}
		const rollPool = buildRollPool(activeCollection)
		if (!rollPool.length) {
			setChosenGame(undefined)
			setRollVisual(null)
			setRollCoverLoading(false)
			return
		}
		const winner = rollPool[randomInt(rollPool.length)]
		setChosenGame(undefined)
		setRollVisual(null)
		rollSessionIdRef.current += 1
		const sessionId = rollSessionIdRef.current

		if (prefersReducedMotion) {
			setChosenGame(winner)
			setRollCoverLoading(false)
			return
		}
		setRollCoverLoading(true)
		const winnerId = String(winner['@_objectid'])
		enrichRollPoolWithThingCovers(rollPool)
			.then((enriched) => {
				if (sessionId !== rollSessionIdRef.current) return
				setRollCoverLoading(false)
				const winnerEnriched = enriched.find((g) => String(g['@_objectid']) === winnerId) || winner
				setRollVisual({ rollPool: enriched, winner: winnerEnriched, sessionId })
			})
			.catch(() => {
				if (sessionId !== rollSessionIdRef.current) return
				setRollCoverLoading(false)
				setRollVisual({ rollPool, winner, sessionId })
			})
	}, [activeCollection, buildRollPool, prefersReducedMotion])

	const handleRollComplete = useCallback((winner, sessionId) => {
		if (sessionId !== rollSessionIdRef.current) return
		setChosenGame(winner)
		setRollVisual(null)
	}, [])

	useEffect(() => {
		startRoll()
	}, [activeCollection, startRoll])

	const isRolling = rollVisual != null || rollCoverLoading
	const filtersBusy = loading || applyingExtraFilters

	const requestCollection = useCallback(() => {
		startFetch({
			getRequestConfig: () => {
				const usernames = parseCommaSeparatedUsernames(username)
				if (!usernames.length) return null
				return {
					usernames,
					params: buildOwnedCollectionParams({
						minRating,
						rating,
						minBGGRating,
						rated,
						played,
						comment,
						hideExpansions,
					}),
				}
			},
			onBeforeAttempt: () => {
				resetPipeline()
				clearThingMetaCache()
				setActivecollection([])
				setChosenGame(undefined)
				setRollVisual(null)
				setRollCoverLoading(false)
			},
			onMissingToken: () => {
				toast({
					title: 'BGG API token missing',
					description:
						'Create a token at https://boardgamegeek.com/using_the_xml_api and set REACT_APP_BGG_API_TOKEN in .env, then restart the dev server.',
					status: 'warning',
					duration: 12000,
					isClosable: true,
				})
			},
			onSuccess: (multiCollection) => {
				toast.closeAll()

				if (multiCollection.length === 0) {
					resetPipeline()
					setActivecollection([])
					toast({
						title: 'No games matched',
						description: 'Try relaxing filters or another username.',
						status: 'warning',
						duration: 5000,
						isClosable: true,
					})
					return
				}

				const playerFiltered = filterPlayerCount(multiCollection, numPlayers, minPlayerCount, maxPlayerCount)
				if (playerFiltered.length === 0) {
					resetPipeline()
					setActivecollection([])
					toast({
						title: 'No games matched',
						description: 'BGG returned games, but none match your player-count filters.',
						status: 'warning',
						duration: 6000,
						isClosable: true,
					})
					return
				}

				pendingFetchOutcomeToastRef.current = true
				commitPlayerFiltered(playerFiltered)
			},
			onHardError: () => {
				resetPipeline()
				toast({
					title: 'Could not load collection',
					description: 'Check the username or try again in a moment (BGG can be slow for large collections).',
					status: 'warning',
					duration: 9000,
					isClosable: true,
				})
			},
		})
	}, [
		startFetch,
		username,
		minRating,
		rating,
		minBGGRating,
		rated,
		played,
		comment,
		hideExpansions,
		numPlayers,
		minPlayerCount,
		maxPlayerCount,
		resetPipeline,
		commitPlayerFiltered,
		toast,
	])

	const fetchAndRoll = () => {
		toast({
			title: 'Fetching collection',
			description: 'Large collections can take a few seconds on BGG.',
			status: 'info',
			duration: 4000,
			isClosable: true,
		})
		requestCollection()
	}

	return (
		<VStack spacing={8} align="stretch">
			<VStack spacing={3} align="flex-start">
				<Heading size="lg">Picker</Heading>
				<Text color={bodyMuted} fontSize="md" lineHeight="tall">
					Can’t decide what to play next? Put Your BGG username into the form, hit the roll button and let the fortune choose the game for you!{' '}
					<Link as={RouterLink} to="/how-to" color="brand.500" fontWeight="600">
						How to
					</Link>
				</Text>
			</VStack>

			<VStack spacing={4} align="stretch">
				<VStack spacing={2} align="stretch">
					<FormControl>
						<FormLabel fontSize="sm" fontWeight="600">
							BGG username
						</FormLabel>
						<Input
							size="lg"
							rounded="lg"
							placeholder="e.g. vitek38"
							value={username}
							onChange={(e) => setUsername(e.target.value)}
						/>
					</FormControl>

					<BggCollectionFiltersAccordion>
						<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
							<RatingSelect label="Min. your rating" value={minRating} onChange={(e) => setMinRating(e.target.value)} />
							<RatingSelect label="Max. your rating" value={rating} onChange={(e) => setRating(e.target.value)} />
							<RatingSelect label="Min. BGG rating" value={minBGGRating} onChange={(e) => setMinBGGRating(e.target.value)} />
							<RatingSelect label="Players (exact)" value={numPlayers} onChange={(e) => setNumPlayers(e.target.value)} />
							<RatingSelect label="Min. players" value={minPlayerCount} onChange={(e) => setMinPlayerCount(e.target.value)} />
							<RatingSelect label="Max. players" value={maxPlayerCount} onChange={(e) => setMaxPlayerCount(e.target.value)} />
						</SimpleGrid>
						<Divider my={5} />
						<BggNumericFilters
							maxPlayTime={maxPlayTime}
							onMaxPlayTimeChange={(e) => setMaxPlayTime(e.target.value)}
							disabled={filtersBusy || isRolling}
						/>
						<Divider my={5} />
						<SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
							<Checkbox isChecked={hideExpansions} onChange={() => setHideExpansions(!hideExpansions)} size="md">
								Hide expansions
							</Checkbox>
							<Checkbox isChecked={rated} onChange={() => setRated(!rated)} size="md">
								Rated on BGG
							</Checkbox>
							<Checkbox isChecked={played} onChange={() => setPlayed(!played)} size="md">
								Marked played
							</Checkbox>
							<Checkbox isChecked={comment} onChange={() => setComment(!comment)} size="md">
								Has comment
							</Checkbox>
						</SimpleGrid>
					</BggCollectionFiltersAccordion>
				</VStack>

				<Button
					isDisabled={username.trim().length === 0 || isRolling || applyingExtraFilters}
					colorScheme="brand"
					variant={activeCollection.length > 0 ? 'outline' : 'solid'}
					onClick={fetchAndRoll}
					isLoading={filtersBusy}
					size="lg"
					rounded="lg"
					rightIcon={<Icon as={FaDice} />}
					aria-busy={filtersBusy || isRolling}
				>
					{activeCollection.length > 0 ? 'Reload from BGG & roll' : 'Load collection & roll'}
				</Button>

				{activeCollection.length > 0 ? (
					<VStack spacing={2} align="stretch">
						<Button
							colorScheme="brand"
							variant="ghost"
							onClick={startRoll}
							isDisabled={isRolling}
							size="lg"
							rounded="lg"
							rightIcon={<Icon as={FaDice} />}
							aria-busy={isRolling}
						>
							Roll again
						</Button>
						<Text fontSize="xs" color={stripMuted} lineHeight="short" maxW="lg">
							Reload from BGG fetches a fresh collection from BoardGameGeek. Roll again only picks from the list you
							already have loaded here.
						</Text>
					</VStack>
				) : null}
			</VStack>

			<Box
				id="picker-roll-again"
				aria-hidden="true"
				h="1px"
				w="full"
				maxW="100%"
				flexShrink={0}
				sx={{ scrollMarginTop: { base: '5.5rem', md: '6rem' } }}
			/>

			{rollCoverLoading ? (
				<VStack
					spacing={3}
					align="center"
					py={2}
					w="full"
					minH={`${getPickerStripSectionMinHeight()}px`}
					aria-busy="true"
					role="status"
					aria-live="polite"
					aria-label="Loading cover art for the draw"
				>
					<Text fontSize="xs" fontWeight="600" color={stripMuted} textTransform="uppercase" letterSpacing="0.08em">
						Drawing
					</Text>
					<Box
						w="full"
						maxW={`${STRIP_MAX_W}px`}
						mx="auto"
						alignSelf="center"
						borderWidth="2px"
						borderColor={cardBorder}
						rounded="2xl"
						bg={stripCardBg}
						boxShadow="lg"
						minH={`${STRIP_CARD_BODY_MAX_H + 72}px`}
					>
						<Center flexDirection="column" gap={3} py={10} px={4}>
							<Spinner size="lg" color="brand.500" thickness="4px" />
							<Text fontSize="sm" color={stripMuted} textAlign="center" maxW="md">
								Loading cover art for a sharp preview…
							</Text>
						</Center>
					</Box>
				</VStack>
			) : rollVisual ? (
				<PickerDrawFlip
					rollPool={rollVisual.rollPool}
					winner={rollVisual.winner}
					sessionId={rollVisual.sessionId}
					onComplete={handleRollComplete}
				/>
			) : null}

			{chosenGame && activeCollection.length > 0 ? (
				<Box
					display="flex"
					justifyContent="center"
					w="full"
					maxW={`${STRIP_MAX_W}px`}
					mx="auto"
					alignSelf="center"
					aria-live="polite"
					aria-atomic="true"
				>
					<ChosenGame game={chosenGame} maxW="100%" coverLayout="intrinsic" />
				</Box>
			) : null}
		</VStack>
	)
}
